import styles from './color.module.css';

import type { BreakpointSize } from '../Breakpoint';
import type { ColorName } from './Color';

/** The CSS properties that can set a color. */
export type ColorCSSProperty = keyof Pick<
  CSSStyleDeclaration,
  'background' | 'border'
>;

export type ColorNameResponsive = Partial<Record<BreakpointSize, ColorName>>;

/**
 * Return the CSS classes to style the colors of a component. To be used by
 * components that extend {@link ColorProp}.
 *
 * ```
 * type MyComponentProps = { borderColor: ColorProp; ... };
 * function MyComponent(props: MyComponentProps) {
 *   const colorClassNames = getColorStyles('border', props.borderColor);
 *   return <div className={colorClassNames}>{props.children}</div>;
 * }
 *
 * <MyComponent borderColor="Chickpea0" /> // Will render a div with the class for "Chickpea0" border color.
 * ```
 *
 * @internal
 */
export function getColorStyles(
  colorProperty: ColorCSSProperty,
  color: ColorName | ColorNameResponsive | undefined
) {
  const colorStyles: string[] = [];

  if (typeof color === 'string') {
    color = { xs: color };
  }

  if (color?.xs) {
    colorStyles.push(styles[`xs-${colorProperty}-${color.xs}`]);
  }

  if (color?.sm) {
    colorStyles.push(styles[`sm-${colorProperty}-${color.sm}`]);
  }

  if (color?.md) {
    colorStyles.push(styles[`md-${colorProperty}-${color.md}`]);
  }

  if (color?.lg) {
    colorStyles.push(styles[`lg-${colorProperty}-${color.lg}`]);
  }

  if (color?.xl) {
    colorStyles.push(styles[`xl-${colorProperty}-${color.xl}`]);
  }

  return colorStyles;
}
